import { useForm, UseFormReturn, FieldValues, DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useCallback } from 'react';

interface UseSectionValidationResult<T extends FieldValues> {
  form: UseFormReturn<T>;
  validateAndProceed: (onSuccess: () => void) => Promise<void>;
  isValidating: boolean;
  shake: boolean;
}

export function useBookingSectionValidation<T extends FieldValues>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  defaultValues?: DefaultValues<T>
): UseSectionValidationResult<T> {
  const [shake, setShake] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const form = useForm<T>({
    resolver: zodResolver(schema),
    mode: 'onSubmit', // Per user decision: validate on section completion only
    reValidateMode: 'onSubmit', // Re-validate on submit after first error
    defaultValues,
  });

  const validateAndProceed = useCallback(
    async (onSuccess: () => void) => {
      setIsValidating(true);

      const isValid = await form.trigger(); // Trigger validation

      setIsValidating(false);

      if (isValid) {
        onSuccess();
      } else {
        // Per user decision: shake animation on blocked progression
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    },
    [form]
  );

  return {
    form,
    validateAndProceed,
    isValidating,
    shake,
  };
}
